'use client';

import { useEffect, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Menu, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';

import { LanguageToggleButton } from '~/(tenant)/_components/language-toggle-button';

import type { LandingPageHeaderProps } from '../../_lib/landing-page-layout-registry';

import { DeltaHeaderAccountSection } from './header-account-section';

import '../styles.css';

// The Nadena lockup (white نادينا/NADENA wordmark + colour leaf mark).
// NOT secondary_logo_white.png — that's the "powered by" partner strip.
const DEFAULT_LOGO = '/landing-pages/delta/images/logo-full.png';

// Authenticated-only quick links
const authNavLinks = [
  { labelKey: 'landing:nav.myBookings', fallback: 'MY BOOKINGS', href: '/bookings', icon: Calendar },
  { labelKey: 'landing:nav.myPackages', fallback: 'MY PACKAGES', href: '/packages', icon: Package },
];

// Routes that should always render a solid (non-transparent) navbar
const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/event',
  '/coaches',
  '/membership',
  '/memberships',
];

export function DeltaHeader({ logoUrl }: LandingPageHeaderProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  const isLandingPage = pathname === '/';
  const shouldBeSolid = solidNavbarRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const logo = logoUrl || DEFAULT_LOGO;
  // Public marketing links removed from the navbar; authenticated users still
  // get their quick links (My Bookings / My Packages).
  const desktopLinks = isAuthenticated ? authNavLinks : [];

  return (
    <>
      <header
        className={cn(
          'delta-landing fixed top-0 right-0 left-0 z-50 transition-all duration-500',
          mobileMenuOpen
            ? 'bg-[var(--delta-ink)] pt-3 pb-1'
            : shouldBeSolid || scrolled
              ? 'bg-[var(--delta-ink)] py-3 shadow-sm'
              : // Transparent state still carries a self-contained top-down
                // gradient so the white logo + links stay legible regardless of
                // what sits behind the navbar (bright photos, etc.).
                'bg-gradient-to-b from-[var(--delta-ink)]/85 to-transparent py-5',
        )}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-12">
          {/* Logo */}
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src={logo}
              alt="Nadena"
              width={1324}
              height={561}
              className="h-10 w-auto lg:h-12"
              priority
            />
          </Link>

          {/* Desktop nav — centered */}
          <nav className="hidden items-center gap-8 lg:flex xl:gap-10">
            {desktopLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-medium tracking-[1px] whitespace-nowrap text-white/90 transition-colors duration-300 hover:text-[var(--delta-green)]"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hidden items-center gap-2 lg:flex">
            <LanguageToggleButton className="text-white hover:bg-white/10 hover:text-white" />
            <DeltaHeaderAccountSection />
          </div>

          {/* Mobile: language toggle + menu button */}
          <div className="flex items-center gap-1 lg:hidden">
            <LanguageToggleButton className="text-white hover:bg-white/10 hover:text-white" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="-mr-2 p-2 text-white"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 top-[56px] z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        {/* Mobile menu */}
        <div
          className={cn(
            'absolute top-full right-0 left-0 z-50 overflow-hidden bg-[var(--delta-ink)] transition-all duration-300 lg:hidden',
            mobileMenuOpen ? 'max-h-[500px]' : 'max-h-0',
          )}
        >
          <nav className="flex flex-col px-6 pb-4">
            {isAuthenticated &&
              authNavLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 border-b border-white/5 py-3 text-base font-medium tracking-[1px] text-white/90 transition-colors duration-300 hover:text-[var(--delta-green)]"
                  >
                    <Icon className="h-5 w-5" />
                    {t(link.labelKey, { defaultValue: link.fallback })}
                  </Link>
                );
              })}
            <div className="pt-4">
              <DeltaHeaderAccountSection
                onNavigate={() => setMobileMenuOpen(false)}
              />
            </div>
          </nav>
        </div>
      </header>

      {/* Spacer for the fixed header — landing page hero handles its own top padding */}
      {!isLandingPage && <div className="h-[64px]" />}
    </>
  );
}

function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    });

    return () => listener.subscription.unsubscribe();
  }, [client.auth, queryClient]);

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}
